import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { apiKartuStok, apiKoreksiMutasi, apiGetPengaturan, formatApiErrorDetail } from "@/lib/api";
import { qty, tanggalIndo } from "@/lib/format";
import * as W from "@/lib/waktu";

// Kartu Stok (F1-E): every movement of one bahan with the balance after it.
// Print-friendly (A4); all figures come from the server.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const JENIS = { MASUK: "Masuk", KELUAR: "Keluar", KEMBALI: "Kembali", TERBUANG: "Terbuang", PENYESUAIAN: "Penyesuaian", KOREKSI: "Koreksi", REWORK: "Rework (terbuang karena reject QC)", STOK_AWAL: "Stok awal" };
const REF = { PEMBELIAN: "Pembelian", PEMBELIAN_BATAL: "Batal pembelian", NOTA: "Nota", NOTA_REVISI: "Revisi nota", PEMBATALAN: "Nota batal", OPNAME: "Opname", KOREKSI: "Koreksi penyesuaian", REWORK: "Rework", IMPOR_STOK: "Impor stok awal", IMPOR_STOK_BATAL: "Kembalikan impor stok awal" };

function waktu(iso, tz) {
  try { return W.waktu(iso); }
  catch { return iso; }
}

function Koreksi({ row, onDone }) {
  const [f, setF] = React.useState({ qty_baru: "", alasan: "" });
  const [error, setError] = React.useState(null);
  const send = async () => {
    setError(null);
    try { await apiKoreksiMutasi(row.id, { qty_baru: String(f.qty_baru).replace(",", "."), alasan: f.alasan }); onDone(); }
    catch (e) { setError(errText(e)); }
  };
  return (
    <div className="no-print flex flex-wrap items-center gap-2 py-1" data-testid={`kartu-koreksi-form-${row.id}`}>
      <input data-testid="kartu-koreksi-qty" type="number" step="0.001" placeholder={`nilai benar (sekarang ${qty(row.qty_milli)})`} value={f.qty_baru} onChange={(e) => setF({ ...f, qty_baru: e.target.value })} className="border border-slate-400 px-2 py-1 w-48" />
      <input data-testid="kartu-koreksi-alasan" placeholder="alasan (wajib)" value={f.alasan} onChange={(e) => setF({ ...f, alasan: e.target.value })} className="border border-slate-400 px-2 py-1 flex-1 min-w-[12rem]" />
      <button data-testid="kartu-koreksi-simpan" disabled={f.qty_baru === "" || f.alasan.trim().length < 5} onClick={send} className="rounded bg-soq-biru px-3 py-1 text-white disabled:opacity-50">Simpan koreksi</button>
      {error && <span className="text-red-600">{error}</span>}
    </div>
  );
}

export default function KartuStok() {
  const { id } = useParams();
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [search] = useSearchParams();  // F1-H: the Laporan menu opens it with a period
  const [filter, setFilter] = React.useState({ dari: search.get("dari") || "", sampai: search.get("sampai") || "" });
  const [d, setD] = React.useState(null);
  const [cfg, setCfg] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [error, setError] = React.useState(null);

  const params = React.useMemo(() => Object.fromEntries(Object.entries(filter).filter(([, v]) => v)), [filter]);
  const load = React.useCallback(async () => {
    try {
      const k = await apiKartuStok(id, params);
      setD(k); setError(null);
      setFilter((f) => (f.dari && f.sampai ? f : { dari: k.dari, sampai: k.sampai }));
    } catch (e) { setError(errText(e)); }
  }, [id, params]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { apiGetPengaturan().then(setCfg).catch(() => {}); }, []);

  const td = "border border-slate-400 px-2 py-1";
  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <style>{"@page { size: A4; margin: 12mm; } @media print { .no-print { display: none !important; } }"}</style>
      <div className="no-print"><TopBar /></div>
      <div className="no-print mx-auto my-4 flex flex-wrap items-end justify-center gap-3 text-sm">
        <Link to="/stok" className="text-slate-700 underline">← Stok Bahan</Link>
        <label>Dari <input data-testid="kartu-dari" type="date" value={filter.dari} onChange={(e) => setFilter({ ...filter, dari: e.target.value })} className="border border-slate-400 px-2 py-1" /></label>
        <label>Sampai <input data-testid="kartu-sampai" type="date" value={filter.sampai} onChange={(e) => setFilter({ ...filter, sampai: e.target.value })} className="border border-slate-400 px-2 py-1" /></label>
        <button data-testid="kartu-print" onClick={() => window.print()} className="rounded bg-soq-biru px-4 py-1.5 text-white">Cetak</button>
      </div>
      {error && <div data-testid="kartu-error" className="text-center text-red-600 p-4">{error}</div>}
      {d && (
        <div data-testid="kartu-paper" className="mx-auto bg-white text-black p-8 text-[11px] leading-snug" style={{ width: "210mm", maxWidth: "100%" }}>
          <div className="text-center">
            <div className="text-base font-bold">{cfg?.nama_usaha || ""}</div>
            <div className="text-sm font-bold">KARTU STOK BAHAN</div>
            <div data-testid="kartu-bahan" className="font-mono">{d.bahan.kode} — {d.bahan.nama} ({d.bahan.satuan})</div>
            <div>Periode {tanggalIndo(d.dari)} s.d. {tanggalIndo(d.sampai)}</div>
          </div>
          <table className="mt-4 w-full border-collapse">
            <thead><tr className="bg-slate-100">
              <th className={td}>Waktu</th><th className={td}>Jenis</th><th className={td}>Referensi</th><th className={td}>Keterangan</th><th className={td}>Oleh</th>
              <th className={`${td} text-right`}>Masuk</th><th className={`${td} text-right`}>Keluar</th><th className={`${td} text-right`}>Saldo</th></tr></thead>
            <tbody>
              <tr><td className={td}>{tanggalIndo(d.dari)}</td><td className={td} colSpan={6}>Saldo awal</td><td data-testid="kartu-saldo-awal" className={`${td} text-right`}>{qty(d.saldo_awal_milli)}</td></tr>
              {d.mutasi.map((m) => (
                <React.Fragment key={m.id}>
                  <tr data-testid={`kartu-row-${m.id}`}>
                    <td className={td}>{waktu(m.waktu, cfg?.zona_waktu)}</td>
                    <td className={td}>{JENIS[m.jenis] || m.jenis}{m.status === "PENDING" ? " (proses)" : ""}</td>
                    <td className={`${td} font-mono`}>{REF[m.referensi?.tipe] || m.referensi?.tipe} {m.referensi?.nomor || ""}{m.referensi?.revisi_ke ? ` (rev ${m.referensi.revisi_ke})` : ""}</td>
                    <td className={td}>{m.alasan || ""}{m.jenis === "TERBUANG" ? ` (terbuang ${qty(m.info_qty_milli)} ${d.bahan.satuan}, stok sudah dipotong saat nota)` : ""}
                      {isOwner && m.jenis === "PENYESUAIAN" && <button data-testid={`kartu-koreksi-${m.id}`} onClick={() => setOpen(open === m.id ? null : m.id)} className="no-print ml-2 underline text-slate-600">koreksi</button>}
                    </td>
                    <td className={td}>{m.oleh?.nama || m.oleh?.username}</td>
                    <td className={`${td} text-right`}>{m.qty_milli > 0 ? qty(m.qty_milli) : ""}</td>
                    <td className={`${td} text-right`}>{m.qty_milli < 0 ? qty(-m.qty_milli) : ""}</td>
                    <td className={`${td} text-right`}>{qty(m.saldo_milli)}</td>
                  </tr>
                  {open === m.id && <tr><td className={td} colSpan={8}><Koreksi row={m} onDone={() => { setOpen(null); load(); }} /></td></tr>}
                </React.Fragment>
              ))}
              {d.mutasi.length === 0 && <tr><td className={`${td} text-center italic`} colSpan={8}>Tidak ada mutasi pada periode ini.</td></tr>}
              <tr className="font-bold bg-slate-50"><td className={td} colSpan={5}>Total periode</td>
                <td data-testid="kartu-masuk" className={`${td} text-right`}>{qty(d.masuk_milli)}</td><td data-testid="kartu-keluar" className={`${td} text-right`}>{qty(d.keluar_milli)}</td>
                <td data-testid="kartu-saldo-akhir" className={`${td} text-right`}>{qty(d.saldo_akhir_milli)}</td></tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] text-slate-500">Dicetak {waktu(new Date().toISOString(), cfg?.zona_waktu)}. Stok hanya berubah lewat mutasi tercatat.</p>
        </div>
      )}
    </div>
  );
}
